/** Starting a learning session or a plan review, and the glossary and questionnaire dialogs. */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BookA, ClipboardList, GraduationCap, MessagesSquare } from "lucide-react";
import { errorMessage, post } from "@/lib/api";
import { openView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RunFlow } from "@/features/ai/RunFlow";
import type { SessionView } from "./LearningView";

export type SessionScope = "paper" | "note" | "manuscript" | "project";

const COPY = {
  learn: {
    title: "Learn",
    description: "Short lessons from this material, each with a few questions; what you miss comes back for review. A note keeps the lessons and the glossary.",
    label: "Why do you want to learn this? (optional: the lessons will fit your goal)",
    placeholder: "e.g. I will use this method in my own study next month",
    start: "Start learning",
  },
  grill: {
    title: "Grill the plan",
    description: "Rounds of questions on the decisions still open, each with a recommended answer. Facts are looked up in the material; only decisions come to you. A note keeps the questions and the decisions.",
    label: "What do you want to decide or stress-test? (optional)",
    placeholder: "e.g. whether the study design answers the research question",
    start: "Start the questions",
  },
} as const;

export function StartSessionDialog({ workspaceId, kind, scope, id, title, onClose }: { workspaceId: string; kind: "learn" | "grill"; scope: SessionScope; id?: string; title: string; onClose(): void }) {
  const qc = useQueryClient();
  const [mission, setMission] = useState("");
  const [busy, setBusy] = useState(false);
  const c = COPY[kind];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {c.title}: {title}
          </DialogTitle>
          <DialogDescription>{c.description}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              const v = await post<SessionView>("/api/learning", { workspaceId, kind, scope, ...(id ? { id } : {}), mission: mission.trim() || undefined });
              await post(`/api/learning/${v.session.id}/next`, {});
              await qc.invalidateQueries({ queryKey: ["learning-list", workspaceId] });
              openView("learning", workspaceId, `${kind === "learn" ? "Learning" : "Plan review"}: ${title}`.slice(0, 80), undefined, v.session.id);
              onClose();
            } catch (err) {
              toast.error(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="session-mission">{c.label}</Label>
            <Textarea id="session-mission" value={mission} onChange={(e) => setMission(e.target.value)} placeholder={c.placeholder} className="min-h-16" maxLength={2000} />
          </div>
          <Button type="submit" disabled={busy}>
            {kind === "learn" ? <GraduationCap /> : <MessagesSquare />} {busy ? "Starting…" : c.start}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "Learn" or "Grill the plan" as a button. */
export function SessionButton(props: { workspaceId: string; kind: "learn" | "grill"; scope: SessionScope; id?: string; title: string; size?: "sm" | "xs"; variant?: "default" | "outline" | "ghost"; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size={props.size ?? "sm"} variant={props.variant ?? "outline"} onClick={() => setOpen(true)} title={COPY[props.kind].description}>
        {props.kind === "learn" ? <GraduationCap /> : <MessagesSquare />} {props.label ?? (props.kind === "learn" ? "Learn" : "Grill the plan")}
      </Button>
      {open && <StartSessionDialog {...props} onClose={() => setOpen(false)} />}
    </>
  );
}

/** "Build the project glossary": terms proposed from the project, to review and add to the Glossary note. */
export function GlossaryDialog({ workspaceId, onClose }: { workspaceId: string; onClose(): void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Project glossary</DialogTitle>
          <DialogDescription>
            One term for one idea: the terms this project uses, what they mean here, and the words to avoid for the same idea. Choose the ones to keep: they go to the “Glossary” note, which the Terminology check and the AI
            writing use.
          </DialogDescription>
        </DialogHeader>
        <RunFlow action="glossary" workspaceId={workspaceId} params={{}} onBack={onClose} />
      </DialogContent>
    </Dialog>
  );
}

export function GlossaryButton({ workspaceId }: { workspaceId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title="The project's terms, what they mean here and the words to avoid">
        <BookA /> Glossary
      </Button>
      {open && <GlossaryDialog workspaceId={workspaceId} onClose={() => setOpen(false)} />}
    </>
  );
}

/** A questionnaire for one person who knows what you need (a co-author, a collaborator, a data provider). */
export function QuestionnaireFlow({ workspaceId, noteId }: { workspaceId: string; noteId?: string }) {
  const [recipient, setRecipient] = useState("");
  const [need, setNeed] = useState("");
  const [params, setParams] = useState<Record<string, unknown> | null>(null);
  if (params) return <RunFlow action="questionnaire" workspaceId={workspaceId} params={params} onBack={() => setParams(null)} />;
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        setParams({ recipient: recipient.trim(), need: need.trim(), ...(noteId ? { noteId } : {}) });
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="q-recipient">Who is it for? (their role, and what they know that you do not)</Label>
        <Input id="q-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="e.g. Sam, the statistician who ran the first analysis" maxLength={500} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="q-need">What do you need back from them?</Label>
        <Textarea id="q-need" value={need} onChange={(e) => setNeed(e.target.value)} placeholder="e.g. which covariates were adjusted for, and why the night of 12 June was left out" className="min-h-20" maxLength={3000} />
      </div>
      <Button type="submit" disabled={recipient.trim().length < 2 || need.trim().length < 3}>
        <ClipboardList /> Continue
      </Button>
    </form>
  );
}

export function QuestionnaireDialog({ workspaceId, noteId, onClose }: { workspaceId: string; noteId?: string; onClose(): void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Questionnaire for a co-author</DialogTitle>
          <DialogDescription>For a question you cannot answer alone: a questionnaire the person fills in, or that you go through together. It is saved as a note in the “Questionnaires” folder.</DialogDescription>
        </DialogHeader>
        <QuestionnaireFlow workspaceId={workspaceId} noteId={noteId} />
      </DialogContent>
    </Dialog>
  );
}
